import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../components/ui/table';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Upload, Trash2, Edit, FileText, Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { DocumentUploadModal } from '../production/DocumentUploadModal';
import { DocumentDownloadModal } from '../production/DocumentDownloadModal';
import { bulkDeleteConsumable, deleteConsumableDocumentsByDocId } from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { AddNDEConsumableModal } from './AddNDEConsumableModal';
import { DeleteConfirmationDialog } from '../../../../../components/ui/DeleteConfirmationDialog';
import { formatDateOnly } from '../../../../../lib/formatUtils';
import { useNDEFilters } from './Filters/hooks/useNDEFilters';
import { NDEFilters } from './Filters/NDEFilters';
import { fetchConsumablesData, refreshConsumablesData } from '../../../../../redux/Project/registryData/action';

interface NDEConsumableTableProps {
  project: DashboardProject;
  onRefresh?: () => void;
  consumableType: string;
}

export const NDEConsumableTable: React.FC<NDEConsumableTableProps> = ({ project, onRefresh, consumableType }) => {
  const dispatch = useDispatch();
  const projectId = project?.project?.projectId;
  
  // Get consumables data from Redux store
  const registryData = useSelector((state: any) => state.registryData);
  const consumablesDataFromStore = useMemo(() => {
    return projectId ? registryData?.consumablesData?.[projectId] : null;
  }, [registryData?.consumablesData, projectId]);
  const consumablesLoading = registryData?.consumablesLoading || false;

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDocumentDeleteModalOpen, setIsDocumentDeleteModalOpen] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState<any>(null);
  const [consumableToEdit, setConsumableToEdit] = useState<any>(null);
  const [consumableToDelete, setConsumableToDelete] = useState<any>(null);
  const [documentToDelete, setDocumentToDelete] = useState<any>(null);
  const [deleteDocumentLoading, setDeleteDocumentLoading] = useState(false);
  const [consumableList, setConsumableList] = useState<any[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Use the NDE filters hook
  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    uniqueConsumableCategories,
    uniqueConsumableTypes,
    uniqueLotNumbers,
    uniqueSerialNumbers,
    uniqueManufacturers,
    uniqueLocations,
    filteredConsumables,
    hasActiveFilters,
    filteredCount,
    onResetFilters,
  } = useNDEFilters({ consumableList });

  // Fetch consumables when this project has no data yet (don't rely on consumablesLoading;
  // it's global and can block this project from ever fetching)
  useEffect(() => {
    if (!projectId) return;
    const hasNoDataForProject = consumablesDataFromStore == null;
    if (hasNoDataForProject) {
      dispatch(fetchConsumablesData({ projectId }));
    }
  }, [projectId, consumablesDataFromStore, dispatch]);

  // Sync consumable list from store when data or consumableType changes (runs after load completes)
  useEffect(() => {
    if (!consumableType) return;
    if (consumablesDataFromStore == null) {
      setConsumableList([]);
      return;
    }
    console.log('consumablesDataFromStore', consumablesDataFromStore);
    console.log('consumableType', consumableType);
    const consumableTypeKey = `${consumableType} Consumables`;
    const consumables =consumableType ==='NDE' ? consumablesDataFromStore[consumableTypeKey]||[] : consumablesDataFromStore[consumableTypeKey]||[];
    console.log('consumables', consumables);
    setConsumableList(Array.isArray(consumables) ? consumables : []);
  }, [consumablesDataFromStore, consumableType]);

  const handleUpload = (consumable: any) => {
    setSelectedConsumable(consumable);
    setIsUploadModalOpen(true);
  };

  const handleDocumentClick = (consumable: any) => {
    setSelectedConsumable(consumable);
    setIsDownloadModalOpen(true);
  };

  const handleEdit = (consumable: any) => {
    setConsumableToEdit(consumable);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (consumable: any) => {
    setConsumableToDelete(consumable);
    setIsDeleteModalVisible(true);
  };

  const handleUploadComplete = async (response: any) => {
    if (response && selectedConsumable) {
      // Refresh Redux store data
      if (projectId) {
        dispatch(refreshConsumablesData({ projectId }));
      }
      if (onRefresh) {
        onRefresh();
      }
      setIsUploadModalOpen(false);
      setSelectedConsumable(null);
    }
  };

  const handleDocumentDownload = (doc: any) => {
    if (doc && doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    }
  };

  const handleDelete = async () => {
    if (!consumableToDelete || !consumableToDelete.id) {
      console.error('Consumable ID is missing');
      return;
    }

    try {
      const payload = {
        ids: [consumableToDelete.id]
      };
      const result = await bulkDeleteConsumable(payload);
      if (result) {
        // Refresh Redux store data
      if (projectId) {
        dispatch(refreshConsumablesData({ projectId }));
      }
        if (onRefresh) {
          onRefresh();
        }
        setIsDeleteModalVisible(false);
        setConsumableToDelete(null);
      }
    } catch (error) {
      console.error('Failed to delete consumable:', error);
    }
  };

  const handleEditComplete = () => {
    setIsEditModalOpen(false);
    setConsumableToEdit(null);
    // Refresh Redux store data
    if (projectId) {
      dispatch(refreshConsumablesData({ projectId }));
    }
    if (onRefresh) {
      onRefresh();
    }
  };

  // Handle document deletion
  const handleDeleteDocument = (doc: any) => {
    setDocumentToDelete(doc);
    setIsDocumentDeleteModalOpen(true);
  };

  const handleCancelDocumentDelete = () => {
    setIsDocumentDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  const handleConfirmDocumentDelete = async () => {
    if (!documentToDelete || !selectedConsumable) return;

    setDeleteDocumentLoading(true);
    try {
      const docId = documentToDelete.docId || documentToDelete.doc_id || documentToDelete.id;
      
      if (!docId) {
        customNotification.error('Failed to delete document', 'Document ID not found');
        setDeleteDocumentLoading(false);
        return;
      }

      const apiPayload = {
        docIds: [Number(docId)]
      };

      const deleteResponse = await deleteConsumableDocumentsByDocId(apiPayload);

      if (deleteResponse) {
        // Refresh Redux store data
      if (projectId) {
        dispatch(refreshConsumablesData({ projectId }));
      }
        // if (onRefresh) {
        //   onRefresh();
        // }
        setIsDocumentDeleteModalOpen(false);
        setIsDownloadModalOpen(false);
        setSelectedConsumable(null);
        setDocumentToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      customNotification.error('Failed to delete document', 'An error occurred while deleting the document.');
    } finally {
      setDeleteDocumentLoading(false);
    }
  };

  // Helper function to extract documents from nested structure
  const extractDocuments = (documents: any[]): any[] => {
    if (!documents || documents.length === 0) return [];
    
    if (documents[0]?.materials && Array.isArray(documents[0].materials)) {
      return documents.flatMap((docGroup: any) => 
        (docGroup.materials || []).map((doc: any) => ({
          ...doc,
          docRef: docGroup.docRef
        }))
      );
    }
    
    return documents;
  };

  const transformDocumentsForDownload = (documents: any[]) => {
    if (!documents || documents.length === 0) return [];
    
    const flatDocuments = extractDocuments(documents);
    
    return flatDocuments.map((doc: any) => ({
      id: doc.registryDocId || doc.id || doc.docId || Math.random().toString(),
      fileName: doc.fileName || doc.file_name,
      fileUrl: doc.fileUrl || doc.file_url,
      uploadDate: doc.uploadDate || doc.uploadedAt || doc.createdAt,
      uploadedBy: doc.uploadedBy || doc.createdBy,
      docId: doc.docId || doc.doc_id || doc.id
    }));
  };

  if (consumablesLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-[13px] text-heritage-grey">Loading consumables...</div>
      </div>
    );
  }

  if (!hasActiveFilters && consumableList.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-page-bg rounded-full flex items-center justify-center mb-4">
            <span className="text-2xl">📦</span>
          </div>
          <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue mb-2">No consumables found</h3>
          <p className="text-[13px] text-heritage-grey max-w-sm">
            No {consumableType} consumables have been added yet. Click "Add New Consumable" to get started.
          </p>
        </div>

        <AddNDEConsumableModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleEditComplete}
          project={project}
          consumableCategory={`${consumableType} Consumables`}
        />
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* NDE Filters */}
        <NDEFilters
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={onFilterChange}
          uniqueConsumableCategories={uniqueConsumableCategories}
          uniqueConsumableTypes={uniqueConsumableTypes}
          uniqueLotNumbers={uniqueLotNumbers}
          uniqueSerialNumbers={uniqueSerialNumbers}
          uniqueManufacturers={uniqueManufacturers}
          uniqueLocations={uniqueLocations}
          hasActiveFilters={hasActiveFilters}
          filteredCount={filteredConsumables.length}
          onResetFilters={onResetFilters}
          consumableTypeLabel={consumableType}
        />
        <Card>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <div className="overflow-y-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
                <Table containerClassName="">
                  <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>

                      <TableHead>Consumable Type</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Lot Number</TableHead>
                  <TableHead>Serial Number</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Material ID</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-center w-[80px]">Documents</TableHead>
                  <TableHead className="text-center w-[80px] sticky right-0 z-10 bg-white">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConsumables.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-[13px] text-heritage-grey py-8">
                      {hasActiveFilters ? 'No consumables match the current filters.' : 'No consumables found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredConsumables.map((consumable, index) => {
                    const extractedDocs = extractDocuments(consumable.documents || []);
                    const docCount = extractedDocs.length;
                    
                    return (
                      <TableRow key={consumable.id || index}>
                        <TableCell>{consumable.consumable_type || '-'}</TableCell>
                        <TableCell>{consumable.type || '-'}</TableCell>
                        <TableCell>{consumable.lot_number || '-'}</TableCell>
                        <TableCell>{consumable.serial_number || '-'}</TableCell>
                        <TableCell>{consumable.manufacturer || '-'}</TableCell>
                        <TableCell>{formatDateOnly(consumable.exp_date || consumable.expiry_date) || '-'}</TableCell>
                        <TableCell>{consumable.material_id || '-'}</TableCell>
                        <TableCell>{consumable.location || '-'}</TableCell>
                        <TableCell>{consumable.notes || '-'}</TableCell>
                        <TableCell className="text-center w-[80px]">
                          <div className="flex items-center gap-2">
                            {/* <Button
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 w-8 h-8 p-0 flex items-center justify-center"
                              onClick={() => handleUpload(consumable)}
                              title="Upload Document"
                              variant="outline"
                            >
                              <Upload className="h-4 w-4" />
                            </Button> */}
                            {docCount > 0 && (
                              <Button
                                className="flex items-center gap-1 text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                                title="View Documents"
                                variant="outline"
                                onClick={() => handleDocumentClick(consumable)}
                              >
                                <FileText className="h-4 w-4" />
                                {docCount}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center w-[80px] sticky right-0 z-10 bg-white">
                          <div className="flex items-center gap-2 justify-center">
                            <Button
                              onClick={() => handleEdit(consumable)}
                              className="text-blueprint-blue hover:text-blueprint-blue hover:bg-[rgba(0,39,63,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                              title="Edit Consumable"
                              variant="outline"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              className="text-hero-red hover:text-hero-red hover:bg-[rgba(235,28,55,0.08)] w-8 h-8 p-0 flex items-center justify-center"
                              onClick={() => handleDeleteClick(consumable)}
                              title="Delete Consumable"
                              variant="outline"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Modal */}
      {selectedConsumable && isUploadModalOpen && (
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => {
            setIsUploadModalOpen(false);
            setSelectedConsumable(null);
          }}
          onUpload={handleUploadComplete}
          title={`Upload ${consumableType} Consumable Document`}
          acceptedTypes=".pdf,.doc,.docx,.xlsx,.xls"
          multiple={true}
          maxFiles={100}
          buttonText="Select Document"
          label="Document"
          description={`Upload ${consumableType} consumable documents (PDF, Excel, DOC, etc...)`}
          documentType={consumableType === 'Coating' ? 'COATING_CONSUMABLE' : 'NDE_CONSUMABLE'}
        />
      )}

      {/* Download Modal */}
      {selectedConsumable && selectedConsumable.documents && extractDocuments(selectedConsumable.documents).length > 0 && (
        <DocumentDownloadModal
          isOpen={isDownloadModalOpen}
          onClose={() => {
            setIsDownloadModalOpen(false);
            setSelectedConsumable(null);
          }}
          documents={transformDocumentsForDownload(selectedConsumable.documents)}
          onDownload={handleDocumentDownload}
          onDelete={(doc) => handleDeleteDocument(doc)}
          title={`Download ${consumableType} Consumable Document`}
          canDelete={true}
        />
      )}

      {/* Edit Modal */}
      {consumableToEdit && (
        <AddNDEConsumableModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setConsumableToEdit(null);
          }}
          onAdd={handleEditComplete}
          project={project}
          consumableToEdit={consumableToEdit}
          consumableCategory={`${consumableType} Consumables`}
        />
      )}

      {/* Add Modal */}
      <AddNDEConsumableModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleEditComplete}
        project={project}
        consumableCategory={`${consumableType} Consumables`}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={isDeleteModalVisible}
        onClose={() => {
          setIsDeleteModalVisible(false);
          setConsumableToDelete(null);
        }}
        onConfirm={handleDelete}
        itemName={consumableToDelete ? (consumableToDelete.serial_number || consumableToDelete.lot_number) : ''}
        itemType="consumable"
      />

      {/* Document Delete Confirmation Modal */}
      <DeleteConfirmationDialog
        isOpen={isDocumentDeleteModalOpen}
        onClose={handleCancelDocumentDelete}
        onConfirm={handleConfirmDocumentDelete}
        isLoading={deleteDocumentLoading}
        itemToDelete={documentToDelete}
        itemType="Consumable Document"
        itemName={documentToDelete?.fileName || documentToDelete?.file_name || ''}
        title={`Delete ${consumableType} Consumable Document`}
      />
    </>
  );
};

